import { useEffect, useRef } from 'react';

import { getConfigOrNull } from '@/lib/config/config-registry';

/**
 * Wraps the Evo `evo-feature-feedback-collector` OC component.
 *
 * Dynamically creates an `<oc-component>` element pointing at the
 * configured registry, then asks the OC client to render it. No
 * custom feedback UI is built here — all sentiment collection is
 * handled by the Evo web component.
 *
 * Registry base, sentiment-API host, and account token all come from the
 * host-supplied `TemplateBuilderConfig.feedback` (via the config registry)
 * rather than env vars — a host (e.g. WFM) that omits `feedback` gets no
 * widget rather than a crash; the standalone app supplies it from its own
 * env vars when building its `TemplateBuilderConfig` (see `main.tsx`).
 *
 * Ported from the pre-rebuild `apps/template-builder` copy
 * (commit `6203d631`); only config sourcing differs.
 */
export function FeatureFeedback({
  feature,
  product = 'RotareadyTemplateBuilder',
  customerName,
  userId,
  theme = 'light',
  reDisplayAfterDays = 30,
  dismissible = true,
  collapsible,
  label,
  language = 'en-GB',
}: FeatureFeedbackProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // The Evo feedback widget is optional. When embedded in a host (e.g. WFM)
  // that doesn't supply `feedback` config, render nothing rather than
  // reading missing fields. When configured, all three values are present.
  const feedbackConfig = getConfigOrNull()?.feedback;
  const configured = feedbackConfig !== undefined;
  const registryBase = configured ? `${feedbackConfig.ocRegistryUrl}/registry` : '';
  const apiHost = configured ? feedbackConfig.apiHost : '';
  const accountToken = configured ? feedbackConfig.accountToken : '';

  useEffect(() => {
    if (!configured) return;
    if (!containerRef.current) return;

    const params = new URLSearchParams({
      feature,
      product,
      accountToken,
      apiHost,
      customerName,
      userId,
      theme,
      dismissible: String(dismissible),
      reDisplayAfterDays: String(reDisplayAfterDays),
      language,
    });

    if (collapsible !== undefined) params.set('collapsible', collapsible);
    if (label) params.set('label', label);

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute(
      'href',
      `${registryBase}/evo-feature-feedback-collector/1/?${params.toString()}`,
    );

    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(ocComponent);

    window.oc?.renderUnloadedComponents();

    return () => {
      container.innerHTML = '';
    };
  }, [
    configured,
    feature,
    product,
    accountToken,
    apiHost,
    registryBase,
    customerName,
    userId,
    theme,
    dismissible,
    reDisplayAfterDays,
    collapsible,
    label,
    language,
  ]);

  if (!configured) return null;
  return <div ref={containerRef} />;
}

interface FeatureFeedbackProps {
  feature: string;
  product?: string;
  customerName: string;
  userId: string;
  theme?: 'light' | 'dark';
  reDisplayAfterDays?: number;
  dismissible?: boolean;
  collapsible?: string;
  label?: string;
  language?: string;
}
